import type { Flight } from '@/lib/api/types';
import { utmStateLabel } from '@/lib/api/utmState';
import { config } from '@/lib/config';
import { formatDistanceKm, formatDuration, formatMaxAlt, formatStamp } from './columns';

const DASH = '—';

export function FlightParams({ flight: f }: { flight: Flight }) {
  // Only fields the active plane can actually fill. A grid of em-dashes
  // reads as broken; an absent field reads as "not part of this plane".
  const fields: Array<[string, string]> = config.isMock
    ? [
        ['Flight ID', f.flight_id],
        ['Source', f.source === 'historical' ? 'Historical' : 'Planned'],
        ['Start', f.start_at ?? DASH],
        ['End', f.end_at ?? DASH],
        ['Duration', formatDuration(f.duration_s)],
        ['Total Distance', formatDistanceKm(f.total_distance_m)],
        ['Max Alt WGS84', f.max_alt_wgs84_m != null ? `${f.max_alt_wgs84_m.toFixed(1)} m` : DASH],
        ['Max Alt ASL', f.max_alt_asl_m != null ? `${f.max_alt_asl_m.toFixed(1)} m` : DASH],
        ['Max Alt AGL', f.max_alt_agl_m != null ? `${f.max_alt_agl_m.toFixed(1)} m` : DASH],
        ['Max Alt (all)', formatMaxAlt(f)],
        ['Data Points', f.data_point_count == null ? DASH : String(f.data_point_count)],
        ['Connector Type', f.connector ?? DASH],
        ['Synced At', f.synced_at ?? DASH],
      ]
    : [
        ['Flight ID', f.flight_id],
        ['Source', f.source === 'historical' ? 'Historical' : 'Planned'],
        ['Mission', f.mission_name ?? DASH],
        ['Mission Type', f.mission_type ?? DASH],
        ['Location', f.mission_location ?? DASH],
        ['Pilot', f.pilot_name ?? DASH],
        // Mockup wording (fsl-mocks, Flights context): Start / End / Duration.
        // These are still RECORD timestamps in live, not a measured flight
        // window (see the mapper NOTE in lib/api/flights.ts) — the `~` that
        // `duration_approx` puts on the VALUE is what now carries that
        // caveat, since the label no longer does.
        ['Start', f.start_at ?? DASH],
        ['End', f.end_at ?? DASH],
        [
          'Duration',
          f.duration_approx && f.duration_s != null
            ? `~${formatDuration(f.duration_s)}`
            : formatDuration(f.duration_s),
        ],
        ['Scheduled', f.scheduled_begin ? formatStamp(f.scheduled_begin) : DASH],
        ['UTM State', utmStateLabel(f.utm_state) ?? DASH],
        ['UTM Declaration', f.utm_declaration_id ?? DASH],
        ['Dock', f.dock_sn ?? DASH],
        ['FH2 Status', f.fh2_status_label ?? DASH],
        ['Connector Type', f.connector ?? DASH],
        // The telemetry endpoint's own `count`. Distance and max altitudes
        // are absent from that response and are NOT computed from its points,
        // so they stay off this grid rather than sitting here as permanent
        // em-dashes — see the mapper note in lib/api/flights.ts.
        ['Data Points', f.data_point_count == null ? DASH : f.data_point_count.toLocaleString()],
        [
          'Altitude Band',
          f.altitude_band
            ? `${f.altitude_band.min_m.toFixed(0)}–${f.altitude_band.max_m.toFixed(0)} m ${f.altitude_band.datum}`
            : DASH,
        ],
      ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
      {fields.map(([label, value]) => (
        <div key={label} className="rounded-md border border-(--color-border) p-2">
          <div className="text-[0.769em] font-mono truncate" title={value}>
            {value}
          </div>
          <div className="text-[0.615em] text-(--color-text3) uppercase tracking-wide">{label}</div>
        </div>
      ))}
    </div>
  );
}
